//services/Notification.js
import axios from "axios";
import config from "../config";
import { getToken } from "../utils/tokenUtils";

const apiurl = config.API_URL;

//get notification user/notifications/unread-count
export const getUnreadNotificationCount = async () => {
  try {
    const response = await axios.get(`${apiurl}/api/v1/user/notifications/unread-count`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    //console.log("nitis", response.data);
    return response.data;
  } catch (error) {
    return Promise.reject(error.response.data);
  }
};

//get all notifications user/notifications?type=unread&limit=5&offset=0
export const getAllNotifications = async (type, limit, offset) => {
  try {
    const response = await axios.get(`${apiurl}/api/v1/user/notifications?type=${type}&limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error.response.data);
  }
};

// action on notification ( react , unread , delete )
export const actionOnNotification = async (id = '', data) => {
  console.log("data", data);
  try {
    const endpoint = id ? `/api/v1/user/notifications/${id}` : '/api/v1/user/notifications';
    const response = await axios.put(`${apiurl}${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error.response.data);
  }
};

// Push Notification Service
class PushNotificationService {
  constructor() {
    this.publicVapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY; 
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    
    // Detect platform for better handling
    this.platform = this.detectPlatform();
    this.isIOS = this.platform === 'ios';
    this.isAndroid = this.platform === 'android';
    this.isMacSafari = this.platform === 'mac-safari';
    this.isMobile = this.isIOS || this.isAndroid;
    
    // Enhanced standalone detection for Mac Safari PWA
    // Safari PWA on macOS may not always set display-mode correctly
    this.isStandalone = this.detectStandaloneMode();
    
    // Log platform detection for debugging
    console.log('[PushNotificationService] Platform detection:', {
      platform: this.platform,
      isIOS: this.isIOS,
      isAndroid: this.isAndroid,
      isMacSafari: this.isMacSafari,
      isStandalone: this.isStandalone,
      userAgent: navigator.userAgent,
      isSupported: this.isSupported,
      hasPushManager: 'PushManager' in window,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasNotification: 'Notification' in window
    });
  }

  // Detect standalone mode with enhanced support for Mac Safari PWA
  detectStandaloneMode() {
    // Standard standalone detection
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    
    // iOS Safari standalone detection
    if (window.navigator.standalone === true) {
      return true;
    }
    
    // Mac Safari PWA detection - check for Safari-specific PWA indicators
    // Safari PWAs on macOS may use 'fullscreen' or 'minimal-ui' display modes
    if (window.matchMedia('(display-mode: fullscreen)').matches) {
      return true;
    }
    
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return true;
    }
    
    // Additional check: Safari PWA doesn't have certain browser UI elements
    // This is a fallback detection for edge cases
    if (this.isMacSafari && !window.menubar?.visible) {
      return true;
    }
    
    return false;
  }

  // Detect the platform
  detectPlatform() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection (iPhone, iPad, iPod)
    // Note: iPad with iPadOS 13+ may report as Macintosh, so check touch capability
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'ios';
    }
    
    // iPadOS 13+ detection - reports as Macintosh but has touch
    // This must come BEFORE Mac Safari detection
    if (/Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
      return 'ios'; // iPadOS behaves like iOS for push notifications
    }
    
    // Android detection
    if (/android/i.test(userAgent)) {
      return 'android';
    }
    
    // Mac Safari detection (Safari on macOS, NOT Chrome/Firefox which contain "Safari" in UA)
    // Also exclude iPadOS which we already handled above
    if (/Macintosh/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/Firefox/.test(userAgent)) {
      return 'mac-safari';
    }
    
    return 'desktop';
  }

  // Get Safari version for macOS
  getSafariVersion() {
    const match = navigator.userAgent.match(/Version\/(\d+)\.(\d+)/);
    if (match) {
      return parseFloat(`${match[1]}.${match[2]}`);
    }
    return null;
  }

  // Check if push is supported on this platform
  isPushSupported() {
    // iOS Safari only supports push in installed PWAs (iOS 16.4+)
    if (this.isIOS) {
      // Check if running as installed PWA
      if (!this.isStandalone) {
        console.warn('[PushNotificationService] Push notifications on iOS require the app to be installed to home screen');
        return false;
      }
      // Check iOS version (16.4+)
      const iOSVersion = this.getIOSVersion();
      if (iOSVersion && iOSVersion < 16.4) {
        console.warn('[PushNotificationService] Push notifications require iOS 16.4 or later');
        return false;
      }
    }
    
    // Mac Safari - Web Push is supported in Safari 16+ (macOS Ventura+)
    // Unlike iOS, Mac Safari supports Web Push both in browser AND PWA mode
    if (this.isMacSafari) {
      const safariVersion = this.getSafariVersion();
      console.log('[PushNotificationService] Mac Safari detected', {
        isStandalone: this.isStandalone,
        safariVersion: safariVersion,
        hasPushManager: 'PushManager' in window,
        hasNotification: 'Notification' in window,
        notificationPermission: 'Notification' in window ? Notification.permission : 'N/A'
      });
      
      // Mac Safari web push requires Safari 16+ (macOS Ventura+)
      // IMPORTANT: Mac Safari supports Web Push in regular browser mode, not just PWA!
      if (!this.isSupported) {
        console.warn('[PushNotificationService] Mac Safari: Web Push not supported (requires Safari 16+ on macOS Ventura or later)');
        return false;
      }
      
      // Check Safari version - Web Push requires Safari 16+
      if (safariVersion && safariVersion < 16) {
        console.warn('[PushNotificationService] Mac Safari: Web Push requires Safari 16 or later (current: ' + safariVersion + ')');
        return false;
      }
      
      // Mac Safari supports push - return true if PushManager is available
      console.log('[PushNotificationService] Mac Safari: Web Push is supported');
      return true;
    }
    
    return this.isSupported;
  }

  // Check if notification permission can be requested
  // This is separate from isPushSupported() - some browsers can show permission dialog
  // even if full push isn't supported yet (e.g., Safari in browser mode)
  canRequestNotificationPermission() {
    // Check if Notification API exists
    if (!('Notification' in window)) {
      return false;
    }
    
    // For Mac Safari, allow permission request even without full push support
    // This helps collect permission early - push can work once requirements are met
    if (this.isMacSafari) {
      const safariVersion = this.getSafariVersion();
      // Safari 16+ has Notification API
      return safariVersion >= 16;
    }
    
    // For iOS, only allow in standalone PWA mode
    if (this.isIOS) {
      return this.isStandalone;
    }
    
    // For other browsers, check if Notification exists
    return true;
  }

  // Get iOS version
  getIOSVersion() {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      return parseFloat(`${match[1]}.${match[2]}`);
    }
    return null;
  }

  // Generate VAPID keys (run this once on your server)
  // Use: web-push generate-vapid-keys

  // Convert base64 string to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isPushSupported()) {
      if (this.isIOS && !this.isStandalone) {
        console.warn('Push notifications on iOS require installing the app to home screen. Add to Home Screen and try again.');
      } else {
        console.warn('Push notifications are not supported on this device/browser');
      }
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // Add this method to the PushNotificationService class:
  async getVapidKeyFromServer() {
    try {
      const response = await axios.get(`${apiurl}/api/v1/user/push-notifications/vapid-key`);
      if (response.data.vapid_public_key) {
        this.publicVapidKey = response.data.vapid_public_key;
        return true;
      }
    } catch (error) {
      console.error('Error getting VAPID key from server:', error);
    }
    return false;
  }

  // Subscribe to push notifications
  async subscribeToPushNotifications(forceRefresh = false) {
    try {
      console.log('[PushNotificationService] subscribeToPushNotifications started', {
        forceRefresh,
        platform: this.platform,
        isMacSafari: this.isMacSafari,
        isStandalone: this.isStandalone
      });

      if (!this.isPushSupported()) {
        const reason = this.isIOS && !this.isStandalone 
          ? 'iOS requires the app to be installed to home screen for push notifications'
          : this.isMacSafari 
            ? 'Mac Safari push requires Safari 16+ on macOS Ventura or later'
            : 'Push notifications not supported on this device';
        console.error('[PushNotificationService] Push not supported:', reason);
        throw new Error(reason);
      }

      // Check if already subscribed
      console.log('[PushNotificationService] Waiting for service worker ready...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[PushNotificationService] Service worker ready:', registration);
      
      let subscription = await registration.pushManager.getSubscription();
      console.log('[PushNotificationService] Current subscription:', subscription ? 'exists' : 'none');

      // If we have a subscription and not forcing refresh, check if it's still valid
      if (subscription && !forceRefresh) {
        try {
          // Validate with server - if server says expired, we need to re-subscribe
          const serverStatus = await this.getSubscriptionStatus();
          if (!serverStatus.has_subscription || serverStatus.expired) {
            console.log('Server reports subscription expired, re-subscribing...');
            // Unsubscribe the stale local subscription
            await subscription.unsubscribe();
            subscription = null;
          } else {
            console.log('User is already subscribed and valid:', subscription);
            return subscription;
          }
        } catch (statusError) {
          // If we can't check status, try to use existing subscription
          // but also send it to server to ensure it's registered
          console.log('Validating existing subscription with server...');
          await this.sendSubscriptionToServer(subscription);
          return subscription;
        }
      }

      // If forcing refresh, unsubscribe first
      if (subscription && forceRefresh) {
        console.log('[PushNotificationService] Force refresh: unsubscribing existing subscription...');
        await subscription.unsubscribe();
        subscription = null;
      }

      // Subscribe to push notifications with fresh subscription
      console.log('[PushNotificationService] Creating new subscription with VAPID key:', this.publicVapidKey.substring(0, 20) + '...');
      
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey)
        });
        
        console.log('[PushNotificationService] New subscription created:', {
          endpoint: subscription.endpoint.substring(0, 50) + '...',
          isSafari: subscription.endpoint.includes('apple') || subscription.endpoint.includes('safari'),
          isChrome: subscription.endpoint.includes('fcm.googleapis.com'),
          isFirefox: subscription.endpoint.includes('mozilla')
        });
      } catch (subscribeError) {
        console.error('[PushNotificationService] Failed to create subscription:', subscribeError);
        throw subscribeError;
      }

      // Send subscription to server
      console.log('[PushNotificationService] Sending subscription to server...');
      await this.sendSubscriptionToServer(subscription);
      console.log('[PushNotificationService] Subscription sent to server successfully');

      return subscription;
    } catch (error) {
      console.error('[PushNotificationService] Error subscribing to push notifications:', error);
      throw error;
    }
  }

  // Force refresh subscription (useful for mobile where subscriptions expire)
  async refreshSubscription() {
    return this.subscribeToPushNotifications(true);
  }

  // Unsubscribe from push notifications
  async unsubscribeFromPushNotifications() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        console.log('User unsubscribed from push notifications');

        // Remove subscription from server
        await this.removeSubscriptionFromServer(subscription);
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  // Send subscription to server
  async sendSubscriptionToServer(subscription) {
    try {
      // Convert subscription to JSON to get the proper structure
      const subscriptionJSON = subscription.toJSON();
      
      // Safari/Apple Push requires aes128gcm encoding
      // Chrome/Firefox use aesgcm by default but also support aes128gcm
      const isSafariPush = subscription.endpoint.includes('web.push.apple.com') || 
                           subscription.endpoint.includes('apple') ||
                           this.isMacSafari || 
                           this.isIOS;
      
      // Add content encoding - Safari requires aes128gcm
      const subscriptionData = {
        ...subscriptionJSON,
        contentEncoding: isSafariPush ? 'aes128gcm' : 'aes128gcm', // Use aes128gcm for all - it's the modern standard
        platform: this.platform,
        userAgent: navigator.userAgent
      };
      
      console.log('[PushNotificationService] Sending subscription to server:', {
        endpoint: subscriptionData.endpoint?.substring(0, 50) + '...',
        contentEncoding: subscriptionData.contentEncoding,
        platform: subscriptionData.platform,
        isSafariPush
      });
      
      const response = await axios.post(`${apiurl}/api/v1/user/push-notifications/subscribe`, {
        subscription: subscriptionData
      }, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Subscription sent to server:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error sending subscription to server:', error);
      throw error;
    }
  }
  // Remove subscription from server
  async removeSubscriptionFromServer(subscription) {
    try {
      const response = await axios.post(`${apiurl}/api/v1/user/push-notifications/unsubscribe`, {
        subscription: subscription  // Remove JSON.stringify
      }, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Subscription removed from server:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error removing subscription from server:', error);
      throw error;
    }
  }

  // Check subscription status
  async checkSubscriptionStatus() {
    if (!this.isSupported) {
      return { subscribed: false, supported: false };
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      return {
        subscribed: !!subscription,
        supported: true,
        subscription
      };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return { subscribed: false, supported: false, error: error.message };
    }
  }

  // Send test notification
  async sendTestNotification() {
    try {
      const response = await axios.post(`${apiurl}/api/v1/user/push-notifications/test`, {}, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Test notification sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }
  // Add this method to check subscription status:
  async getSubscriptionStatus() {
    try {
      const response = await axios.get(`${apiurl}/api/v1/user/push-notifications/status`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        }
      });

      console.log('Subscription status:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  // Update the constructor to get VAPID key from server:
  async initialize() {
    try {
      // Get VAPID key from server
      const response = await axios.get(`${apiurl}/api/v1/user/push-notifications/vapid-key`);
      if (response.data.vapid_public_key) {
        this.publicVapidKey = response.data.vapid_public_key;
        console.log('VAPID key loaded from server:', this.publicVapidKey);
      }

      // Check and refresh subscription if needed (important for mobile)
      if (this.isSupported && Notification.permission === 'granted') {
        await this.validateAndRefreshSubscription();
      }
    } catch (error) {
      console.warn('Could not load VAPID key from server, using default:', error);
    }
  }

  // Validate subscription and refresh if expired (important for mobile devices)
  async validateAndRefreshSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        console.log('No local subscription found, creating new one...');
        await this.subscribeToPushNotifications(true);
        return;
      }

      // Check with server if subscription is still valid
      try {
        const serverStatus = await this.getSubscriptionStatus();
        if (!serverStatus.has_subscription) {
          console.log('Server has no subscription, re-registering...');
          await this.sendSubscriptionToServer(subscription);
        } else if (serverStatus.expired) {
          console.log('Subscription expired on server, refreshing...');
          await this.refreshSubscription();
        }
      } catch (statusError) {
        // If can't reach server, try to send current subscription
        console.log('Could not check server status, ensuring subscription is registered...');
        await this.sendSubscriptionToServer(subscription);
      }
    } catch (error) {
      console.error('Error validating subscription:', error);
    }
  }

  // Setup periodic subscription refresh (call this on app init)
  setupPeriodicRefresh(intervalMinutes = 60) {
    // Refresh subscription periodically (helps with mobile expiration)
    setInterval(async () => {
      if (this.isSupported && Notification.permission === 'granted') {
        console.log('Periodic subscription validation...');
        await this.validateAndRefreshSubscription();
      }
    }, intervalMinutes * 60 * 1000);
  }
}

// Export push notification service
export const pushNotificationService = new PushNotificationService();

// Expose globally for debugging in Safari console
if (typeof window !== 'undefined') {
  window.pushNotificationService = pushNotificationService;
  window.debugPushNotifications = async () => {
    console.log('=== Push Notification Debug Info ===');
    console.log('Browser:', navigator.userAgent);
    console.log('Platform:', pushNotificationService.platform);
    console.log('Is Mac Safari:', pushNotificationService.isMacSafari);
    console.log('Is iOS:', pushNotificationService.isIOS);
    console.log('Standalone Mode:', pushNotificationService.isStandalone);
    console.log('Push Supported:', pushNotificationService.isPushSupported());
    console.log('Notification API:', 'Notification' in window);
    console.log('ServiceWorker API:', 'serviceWorker' in navigator);
    console.log('PushManager API:', 'PushManager' in window);
    console.log('Notification Permission:', Notification.permission);
    
    // Check service worker
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      console.log('SW Registration:', registration ? 'Found' : 'Not Found');
      if (registration) {
        console.log('SW State:', registration.active?.state);
        console.log('SW Scope:', registration.scope);
        const subscription = await registration.pushManager?.getSubscription();
        console.log('Push Subscription:', subscription ? 'Exists' : 'None');
        if (subscription) {
          console.log('Endpoint:', subscription.endpoint);
          console.log('Expiration:', subscription.expirationTime);
        }
      }
    }
    console.log('=== End Debug Info ===');
  };
  
  // Add a test subscribe function that can be called from a button click handler
  window.testPushSubscribe = async () => {
    try {
      console.log('Starting push subscription test...');
      await pushNotificationService.initialize();
      const result = await pushNotificationService.subscribeToPushNotifications();
      console.log('Subscribe result:', result);
      return result;
    } catch (error) {
      console.error('Subscribe error:', error);
      throw error;
    }
  };
  
  // Create a floating button for testing push notifications in Safari
  // This creates a button that can be clicked to trigger permission request
  window.showPushTestButton = () => {
    // Remove existing button if any
    const existing = document.getElementById('push-test-btn');
    if (existing) existing.remove();
    
    const btn = document.createElement('button');
    btn.id = 'push-test-btn';
    btn.innerText = '🔔 Enable Push Notifications';
    btn.style.cssText = 'position:fixed;top:10px;right:10px;z-index:99999;padding:15px 20px;background:#1976d2;color:white;border:none;border-radius:8px;cursor:pointer;font-size:16px;box-shadow:0 4px 12px rgba(0,0,0,0.3);';
    
    btn.onclick = async () => {
      try {
        btn.innerText = 'Requesting permission...';
        const permission = await Notification.requestPermission();
        console.log('Permission result:', permission);
        
        if (permission === 'granted') {
          btn.innerText = 'Subscribing...';
          await pushNotificationService.initialize();
          const result = await pushNotificationService.subscribeToPushNotifications();
          console.log('Subscribe result:', result);
          btn.innerText = '✅ Subscribed!';
          btn.style.background = 'green';
          setTimeout(() => btn.remove(), 3000);
        } else {
          btn.innerText = '❌ Permission: ' + permission;
          btn.style.background = 'red';
        }
      } catch (error) {
        console.error('Error:', error);
        btn.innerText = '❌ Error: ' + error.message;
        btn.style.background = 'red';
      }
    };
    
    document.body.appendChild(btn);
    console.log('Push test button added to page. Click it to enable notifications.');
  };
  
  // Auto-run debug on load if in Safari
  if (pushNotificationService.isMacSafari) {
    console.log('[Safari] Push notification service loaded. Run window.debugPushNotifications() for full info.');
    console.log('[Safari] To test push notifications, run: window.showPushTestButton()');
  }
}


